/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useLayer, useHover, Arrow } from 'react-laag';
import ResizeObserver from 'resize-observer-polyfill';
import { BodyText } from '../typography';

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const TooltipBox = styled(BodyText)`
  color: ${({ textColor }) => textColor};
  border-radius: 4px;
  padding: 0 8px;
  z-index: 100000;
  font-size: 12px;
  font-weight: 400;
  width: ${({ width }) => width || 'auto'};
  height: ${({ height }) => height || 'auto'};
  white-space: ${({ whiteSpace }) => whiteSpace || 'normal'};
  text-align: center;
  opacity: 0;
  background-color: transparent;
  transform-origin: center bottom;
  ${({ backgroundColor, borderColor }) => css`
    @keyframes backgroundFade {
      0% {
        background-color: transparent;
        border-color: transparent;
      }
      100% {
        background-color: ${backgroundColor};
        border-color: ${borderColor};
      }
    }
    animation: ${fadeIn} 0.25s ease-out forwards, backgroundFade 0.3s ease-out forwards;
  `}
`;

const TooltipText = styled.span`
  ${({ alignChildren }) =>
    alignChildren &&
    css`
      display: flex;
      align-items: center;
    `}
`;

function Tooltip({
  children,
  text = '',
  dataPanel = '',
  position = 'top-center',
  isMobile = false,
  forceHide = false,
  offset = 15,
  delay = 300,
  delayOut = 200,
  sizeArrow,
  autoPlacements,
  allowPlacements,
  width,
  height,
  whiteSpace,
  backgroundColor = 'black',
  textColor = 'white',
  borderColor = 'black',
  alignChildren,
  showChildren = true
}) {
  const [show, hoverProps, close] = useHover({ delayEnter: delay, delayLeave: delayOut });

  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen: show,
    placement: position,
    triggerOffset: offset,
    ResizeObserver,
    auto: autoPlacements,
    possiblePlacements: allowPlacements
  });

  useEffect(() => {
    if (forceHide) {
      close();
    }
  }, [close, forceHide]);

  if (isMobile || !showChildren) {
    return <>{children}</>;
  }

  let trigger;
  if (text) {
    trigger = (
      <TooltipText {...triggerProps} {...hoverProps} data-panel={dataPanel} alignChildren={alignChildren}>
        {children}
      </TooltipText>
    );
  } else {
    // In case of an react-element, we need to clone it in order to attach our own props
    trigger = React.cloneElement(children, {
      ...triggerProps,
      ...hoverProps
    });
  }

  return (
    <>
      {trigger}
      {renderLayer(
        <span>
          {show && (
            <TooltipBox
              {...layerProps}
              width={width}
              height={height}
              whiteSpace={whiteSpace}
              backgroundColor={backgroundColor}
              borderColor={borderColor}
              textColor={textColor}
              data-panel={dataPanel}
            >
              {text}
              <Arrow
                {...arrowProps}
                borderWidth={1}
                size={sizeArrow || 5}
                backgroundColor={backgroundColor}
                borderColor={borderColor}
              />
            </TooltipBox>
          )}
        </span>
      )}
    </>
  );
}

export default Tooltip;
